import { Row } from '@/lib/utils';
import { MediumText, RegularText } from '@/lib/Text';
import { BrushType, WorkTemplate } from '../types/workingHours';

export type BrushOption = {
    id: string;
    label: string;
    brush: BrushType;
    accent: string;
    bg: string;
    text: string;
};

export const BUILT_IN_BRUSHES: BrushOption[] = [
    {
        id: 'default',
        // 셀을 "기본" 상태로 돌려 놓는 액션 — 옅은 파랑 (기본 근무와 동일)
        label: '기본으로 되돌리기',
        brush: { kind: 'default' },
        accent: '#5B8AD1',
        bg: '#D6E4F7',
        text: '#2A4A80',
    },
    {
        id: 'include',
        // 셀을 IN(추가 근무)으로 — 옅은 초록 (셀과 동일)
        label: '추가 근무 (IN)',
        brush: { kind: 'include' },
        accent: '#166E3F',
        bg: '#C3E8CF',
        text: '#166E3F',
    },
    {
        id: 'exclude',
        // 셀을 OUT(휴무)으로 — 옅은 빨강 (셀과 동일)
        label: '휴무 (OUT)',
        brush: { kind: 'exclude' },
        accent: '#8B1B2A',
        bg: '#F8C8CC',
        text: '#8B1B2A',
    },
];

/** 사용자 템플릿 전용 팔레트 — 순서대로 5색 순환 */
const TEMPLATE_PALETTE: { bg: string; accent: string; text: string }[] = [
    { bg: '#EBDEFC', accent: '#6C3FD6', text: '#4B2599' }, // 1. 보라
    { bg: '#CDEAE6', accent: '#1E7E70', text: '#125146' }, // 2. 청록
    { bg: '#FDE0C1', accent: '#C76A20', text: '#8A470F' }, // 3. 주황
    { bg: '#FBD0E1', accent: '#B52E6E', text: '#7B1B4A' }, // 4. 핑크
    { bg: '#F6E7A2', accent: '#8A6A10', text: '#5F4905' }, // 5. 금색
];

const templateToBrushOption = (t: WorkTemplate, index: number): BrushOption => {
    const palette = TEMPLATE_PALETTE[index % TEMPLATE_PALETTE.length];
    if (t.type === 'include') {
        return {
            id: `tpl-${t.id}`,
            label: t.name,
            brush: { kind: 'include', startTime: t.startTime, endTime: t.endTime, memo: t.memo },
            accent: palette.accent,
            bg: palette.bg,
            text: palette.text,
        };
    }
    return {
        id: `tpl-${t.id}`,
        label: t.name,
        brush: { kind: 'exclude', memo: t.memo },
        accent: palette.accent,
        bg: palette.bg,
        text: palette.text,
    };
};

type Props = {
    templates?: WorkTemplate[];
    selectedBrushId: string | null;
    onSelect: (brush: BrushOption | null) => void;
    onOpenTemplateManager?: () => void;
};

export const WorkBrushPalette = ({
    templates = [],
    selectedBrushId,
    onSelect,
    onOpenTemplateManager,
}: Props) => {
    const allOptions: BrushOption[] = [
        ...BUILT_IN_BRUSHES,
        ...templates.map((t, idx) => templateToBrushOption(t, idx)),
    ];

    return (
        <Row className="items-center gap-[8px] flex-wrap">
            <MediumText fontSize={13} color="#4B5563" className="mr-[4px]">
                적용할 근무 유형
            </MediumText>
            {allOptions.map((opt) => {
                const selected = selectedBrushId === opt.id;
                return (
                    <div
                        key={opt.id}
                        className={`h-[34px] px-[14px] rounded-[999px] flex items-center gap-[6px] cursor-pointer transition-all select-none ${
                            selected
                                ? 'ring-[2px] ring-[#6D5FD8] shadow-[0_2px_6px_rgba(109,95,216,0.25)] scale-[1.03]'
                                : 'hover:brightness-95'
                        }`}
                        style={{ backgroundColor: opt.bg }}
                        onClick={() => onSelect(selected ? null : opt)}
                    >
                        <div className="w-[10px] h-[10px] rounded-full" style={{ backgroundColor: opt.accent }} />
                        <RegularText fontSize={13} color={opt.text}>
                            {opt.label}
                        </RegularText>
                        {selected && (
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="ml-[2px]">
                                <path
                                    d="M2 6.5L5 9L10 3"
                                    stroke="#6D5FD8"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        )}
                    </div>
                );
            })}
            {onOpenTemplateManager && (
                <div
                    className="h-[34px] px-[14px] rounded-[999px] flex items-center gap-[4px] cursor-pointer bg-white hover:bg-[#F7F8FA] transition-colors"
                    onClick={onOpenTemplateManager}
                >
                    <RegularText fontSize={13} color="#6B7280">
                        + 자주 쓰는 유형 관리
                    </RegularText>
                </div>
            )}
            {selectedBrushId && (
                <RegularText fontSize={13} color="#6D5FD8" className="ml-[4px]">
                    · 셀에 클릭 또는 드래그로 적용
                </RegularText>
            )}
        </Row>
    );
};

// 편의 export: 기존 이름과 호환
export const DEFAULT_BRUSH_OPTIONS = BUILT_IN_BRUSHES;
